export type Urgency = 'urgent' | 'attention' | 'normal'

export type PriorityCategory =
  | 'lead'
  | 'realtor'
  | 'preapproval'
  | 'milestone'
  | 'task'

export interface RecordItem {
  id: string
  name: string
  primary: string
  secondary: string
  tag: string
  urgency?: Urgency
}

export interface RecordSet {
  id: string
  title: string
  subtitle: string
  records: RecordItem[]
  /** Simulates a failed data fetch on first load so the error + retry state is reachable. */
  failFirst?: boolean
}

export interface Priority {
  id: string
  category: PriorityCategory
  count: number
  label: string
  description: string
  urgency: Urgency
  recordSetId: string
}

export interface Goal {
  id: string
  label: string
  current: number
  target: number
  unit: string
  /** Inverted goals count down to zero (e.g. overdue tasks). */
  inverted?: boolean
  recordSetId: string
}

export interface Metric {
  id: string
  label: string
  value: string
  support: string
  comparison?: { text: string; direction: 'up' | 'down' | 'flat' }
  progress?: { current: number; target: number }
  tone: 'brand' | 'teal' | 'danger' | 'navy'
  recordSetId: string
}

export const loanOfficer = {
  firstName: 'Jordan',
  fullName: 'Jordan Reyes',
  title: 'Senior Loan Officer · NMLS #1184629',
  branch: 'Summit Home Lending — Denver, CO',
}

export const dailySummary = {
  completionPercent: 72,
  goalsCompleted: 4,
  goalsTotal: 6,
  lastSyncedLabel: 'Synced 4 minutes ago',
}

export const recordSets: Record<string, RecordSet> = {
  'new-leads': {
    id: 'new-leads',
    title: '4 new leads need contact',
    subtitle: 'Purchase and refinance inquiries received in the last 24 hours',
    records: [
      {
        id: 'L-40219',
        name: 'Morgan Taylor',
        primary: 'Purchase · $485,000 · Conventional',
        secondary: 'Received 18 minutes ago · Zillow inquiry · Denver, CO',
        tag: 'Never contacted',
        urgency: 'urgent',
      },
      {
        id: 'L-40214',
        name: 'Priya Raman',
        primary: 'Purchase · $612,500 · Jumbo',
        secondary: 'Received 2 hours ago · Realtor referral (Dana Whitfield)',
        tag: 'Never contacted',
        urgency: 'urgent',
      },
      {
        id: 'L-40208',
        name: 'Devin Brooks',
        primary: 'Refinance · $329,000 · FHA streamline',
        secondary: 'Received 6 hours ago · Website rate quote',
        tag: 'Voicemail left',
        urgency: 'attention',
      },
      {
        id: 'L-40201',
        name: 'Alicia & Marco Serrano',
        primary: 'Purchase · $398,750 · VA',
        secondary: 'Received yesterday 7:42 PM · Open house sign-in',
        tag: 'Email sent',
        urgency: 'attention',
      },
    ],
  },
  'realtor-followups': {
    id: 'realtor-followups',
    title: '7 Realtor follow-ups are due',
    subtitle: 'Partner touches scheduled or overdue for today',
    failFirst: true,
    records: [
      {
        id: 'A-2201',
        name: 'Dana Whitfield · Compass',
        primary: '3 shared buyers in process',
        secondary: 'Last touch 9 days ago · Co-marketing flyer promised',
        tag: 'Due today',
        urgency: 'attention',
      },
      {
        id: 'A-2188',
        name: 'Curtis Nakamura · Keller Williams',
        primary: '1 pre-approval expiring Friday',
        secondary: 'Last touch 12 days ago · Wants updated payment scenario',
        tag: 'Overdue',
        urgency: 'urgent',
      },
      {
        id: 'A-2176',
        name: 'Renee Ortiz · RE/MAX Alliance',
        primary: '2 shared buyers in process',
        secondary: 'Last touch 5 days ago · Listing appointment Thursday',
        tag: 'Due today',
        urgency: 'normal',
      },
      {
        id: 'A-2170',
        name: 'Trent Mabry · eXp Realty',
        primary: 'New partner — first 90 days',
        secondary: 'Last touch 4 days ago · Intro to construction loans',
        tag: 'Due today',
        urgency: 'normal',
      },
      {
        id: 'A-2165',
        name: 'Sofia Delgado · Coldwell Banker',
        primary: '4 shared buyers in process',
        secondary: 'Last touch 8 days ago · Asked about 2-1 buydown',
        tag: 'Due today',
        urgency: 'attention',
      },
      {
        id: 'A-2151',
        name: 'Wes Cardoza · Redfin',
        primary: '1 shared buyer in underwriting',
        secondary: 'Last touch 3 days ago · Needs milestone update',
        tag: 'Due today',
        urgency: 'normal',
      },
      {
        id: 'A-2140',
        name: 'Hannah Pierce · Berkshire Hathaway',
        primary: 'Top referral partner — 11 YTD',
        secondary: 'Last touch 15 days ago · Quarterly business review',
        tag: 'Overdue',
        urgency: 'urgent',
      },
    ],
  },
  'preapproval-reviews': {
    id: 'preapproval-reviews',
    title: '3 pre-approvals need review',
    subtitle: 'Submitted applications awaiting your credit and income review',
    records: [
      {
        id: 'OPP-88342',
        name: 'Kelsey Vaughn',
        primary: '$512,000 · 5.5% down · DTI 41%',
        secondary: 'Submitted 3 hours ago · Credit 728 · Docs complete',
        tag: 'Ready to review',
        urgency: 'attention',
      },
      {
        id: 'OPP-88317',
        name: 'Owen Machado',
        primary: '$367,400 · FHA · DTI 46%',
        secondary: 'Submitted yesterday · Missing 2024 W-2',
        tag: 'Docs outstanding',
        urgency: 'attention',
      },
      {
        id: 'OPP-88295',
        name: 'Beatrice Lund',
        primary: '$744,900 · Jumbo · DTI 38%',
        secondary: 'Submitted yesterday · Expires in 6 days',
        tag: 'Expiring soon',
        urgency: 'urgent',
      },
    ],
  },
  'milestone-updates': {
    id: 'milestone-updates',
    title: '2 loans require milestone updates',
    subtitle: 'Borrowers and partners are waiting on a status change',
    records: [
      {
        id: 'LN-70118',
        name: 'Nathaniel Okafor',
        primary: '$429,000 · Conventional · Closing Aug 12',
        secondary: 'Appraisal received — milestone not advanced (2 days)',
        tag: 'Update needed',
        urgency: 'attention',
      },
      {
        id: 'LN-70092',
        name: 'Grace Lindqvist',
        primary: '$556,250 · Jumbo · Closing Aug 05',
        secondary: 'Clear to close issued — borrower not notified',
        tag: 'Update needed',
        urgency: 'attention',
      },
    ],
  },
  'overdue-tasks': {
    id: 'overdue-tasks',
    title: '3 overdue tasks need resolution',
    subtitle: 'Past-due activities assigned to you',
    records: [
      {
        id: 'T-9921',
        name: 'Send updated rate scenario — Curtis Nakamura',
        primary: 'Due 2 days ago',
        secondary: 'Related to OPP-88295 · Beatrice Lund',
        tag: 'Overdue 2d',
        urgency: 'urgent',
      },
      {
        id: 'T-9908',
        name: 'Collect bank statements — Owen Machado',
        primary: 'Due 3 days ago',
        secondary: 'Related to OPP-88317 · Blocking underwriting',
        tag: 'Overdue 3d',
        urgency: 'urgent',
      },
      {
        id: 'T-9884',
        name: 'Quarterly business review — Hannah Pierce',
        primary: 'Due 5 days ago',
        secondary: 'Related to A-2140 · Top referral partner',
        tag: 'Overdue 5d',
        urgency: 'urgent',
      },
    ],
  },
  'consumer-conversations': {
    id: 'consumer-conversations',
    title: 'Consumer conversations today',
    subtitle: '12 of 20 logged calls and meaningful conversations',
    records: [
      {
        id: 'C-5512',
        name: 'Yolanda Frey',
        primary: 'Outbound call · 11 min',
        secondary: '8:14 AM · Discussed 2-1 buydown on $402,000 purchase',
        tag: 'Logged',
      },
      {
        id: 'C-5510',
        name: 'Ibrahim Sallah',
        primary: 'Inbound call · 6 min',
        secondary: '8:41 AM · Rate lock question, follow-up scheduled',
        tag: 'Logged',
      },
      {
        id: 'C-5507',
        name: 'Meredith Cole',
        primary: 'Outbound call · 4 min',
        secondary: '9:02 AM · Confirmed appraisal appointment',
        tag: 'Logged',
      },
      {
        id: 'C-5504',
        name: 'Trevor Nguyen',
        primary: 'Video meeting · 22 min',
        secondary: '9:30 AM · Full application walkthrough, $588,000',
        tag: 'Logged',
      },
    ],
  },
  'realtor-touches': {
    id: 'realtor-touches',
    title: 'Realtor touches today',
    subtitle: '9 of 15 partner touches logged',
    records: [
      {
        id: 'RT-3301',
        name: 'Renee Ortiz · RE/MAX Alliance',
        primary: 'Call · 7 min',
        secondary: '8:20 AM · Reviewed buyer pipeline for August',
        tag: 'Logged',
      },
      {
        id: 'RT-3298',
        name: 'Wes Cardoza · Redfin',
        primary: 'Text · outbound',
        secondary: '8:35 AM · Sent underwriting status for shared buyer',
        tag: 'Logged',
      },
      {
        id: 'RT-3294',
        name: 'Trent Mabry · eXp Realty',
        primary: 'Email · outbound',
        secondary: '9:05 AM · Construction loan one-pager',
        tag: 'Logged',
      },
    ],
  },
  'partner-meetings': {
    id: 'partner-meetings',
    title: 'Partner meetings today',
    subtitle: '1 of 2 meetings completed',
    records: [
      {
        id: 'EV-1180',
        name: 'Dana Whitfield · Compass',
        primary: 'Completed · 8:00–8:45 AM',
        secondary: 'Co-marketing plan for fall listings',
        tag: 'Completed',
      },
      {
        id: 'EV-1184',
        name: 'Sofia Delgado · Coldwell Banker',
        primary: 'Upcoming · 3:30–4:15 PM',
        secondary: 'Buydown strategy for two active buyers',
        tag: 'Scheduled',
      },
    ],
  },
  'priority-followups': {
    id: 'priority-followups',
    title: 'Priority follow-ups',
    subtitle: 'Follow-ups flagged as must-complete today',
    records: [],
  },
  'daily-score': {
    id: 'daily-score',
    title: 'Daily score breakdown',
    subtitle: 'How today’s 72% is calculated across activity categories',
    records: [
      {
        id: 'S-1',
        name: 'Consumer conversations',
        primary: '30 of 40 points',
        secondary: '12 of 20 conversations logged',
        tag: '75%',
      },
      {
        id: 'S-2',
        name: 'Realtor touches',
        primary: '18 of 30 points',
        secondary: '9 of 15 touches logged',
        tag: '60%',
      },
      {
        id: 'S-3',
        name: 'Pre-approval reviews',
        primary: '15 of 15 points',
        secondary: '3 of 3 reviews completed',
        tag: '100%',
      },
      {
        id: 'S-4',
        name: 'Task hygiene',
        primary: '9 of 15 points',
        secondary: '3 tasks still overdue',
        tag: '60%',
      },
    ],
  },
  'activities-remaining': {
    id: 'activities-remaining',
    title: '11 activities remaining',
    subtitle: 'Open activities scheduled for the rest of today',
    records: [
      {
        id: 'T-9950',
        name: 'Call Morgan Taylor',
        primary: 'Due 10:00 AM',
        secondary: 'New purchase lead · $485,000',
        tag: 'Next up',
        urgency: 'urgent',
      },
      {
        id: 'T-9951',
        name: 'Review pre-approval — Kelsey Vaughn',
        primary: 'Due 11:30 AM',
        secondary: 'OPP-88342 · Docs complete',
        tag: 'Scheduled',
        urgency: 'attention',
      },
      {
        id: 'T-9952',
        name: 'Advance milestone — Nathaniel Okafor',
        primary: 'Due 1:00 PM',
        secondary: 'LN-70118 · Appraisal received',
        tag: 'Scheduled',
      },
      {
        id: 'T-9953',
        name: 'Partner meeting — Sofia Delgado',
        primary: '3:30 PM',
        secondary: 'Buydown strategy discussion',
        tag: 'Scheduled',
      },
      {
        id: 'T-9954',
        name: 'Send closing recap — Grace Lindqvist',
        primary: 'Due 4:30 PM',
        secondary: 'LN-70092 · Clear to close',
        tag: 'Scheduled',
      },
    ],
  },
  'goals-completed': {
    id: 'goals-completed',
    title: '4 of 6 daily goals on track',
    subtitle: 'Goal status for Tuesday, July 28 · 2 need attention',
    records: [
      {
        id: 'G-4',
        name: 'Pre-approval reviews',
        primary: '3 of 3 reviews',
        secondary: 'Goal met at 9:48 AM',
        tag: 'Complete',
      },
      {
        id: 'G-6',
        name: 'Priority follow-ups',
        primary: '5 of 5 follow-ups',
        secondary: 'Goal met at 9:20 AM',
        tag: 'Complete',
      },
      {
        id: 'G-1',
        name: 'Consumer conversations',
        primary: '12 of 20 conversations',
        secondary: 'On pace — 8 remaining before 5:00 PM',
        tag: 'On pace',
      },
      {
        id: 'G-2',
        name: 'Realtor touches',
        primary: '9 of 15 touches',
        secondary: 'On pace — 6 remaining before 5:00 PM',
        tag: 'On pace',
      },
      {
        id: 'G-3',
        name: 'Partner meetings',
        primary: '1 of 2 meetings',
        secondary: 'Second meeting not yet confirmed for 3:30 PM',
        tag: 'Needs attention',
        urgency: 'attention',
      },
      {
        id: 'G-5',
        name: 'Zero overdue tasks',
        primary: '3 tasks still overdue',
        secondary: 'Oldest overdue 5 days · blocking a partner review',
        tag: 'At risk',
        urgency: 'urgent',
      },
    ],
  },
}

export const metrics: Metric[] = [
  {
    id: 'daily-score',
    label: 'Daily Score',
    value: '72%',
    support: 'Perfect Day completion',
    comparison: { text: '+8% vs. 30-day average', direction: 'up' },
    tone: 'brand',
    recordSetId: 'daily-score',
  },
  {
    id: 'goals-completed',
    label: 'Goals Completed',
    value: '4 of 6',
    support: '4 complete or on pace · 2 need attention',
    progress: { current: 4, target: 6 },
    tone: 'teal',
    recordSetId: 'goals-completed',
  },
  {
    id: 'overdue-tasks',
    label: 'Overdue Tasks',
    value: '3',
    support: 'Oldest overdue 5 days',
    comparison: { text: '+1 since yesterday', direction: 'down' },
    tone: 'danger',
    recordSetId: 'overdue-tasks',
  },
  {
    id: 'activities-remaining',
    label: 'Activities Remaining',
    value: '11',
    support: 'Scheduled before 5:00 PM',
    comparison: { text: 'On pace to finish by 4:40 PM', direction: 'flat' },
    tone: 'navy',
    recordSetId: 'activities-remaining',
  },
]

export const priorities: Priority[] = [
  {
    id: 'p-leads',
    category: 'lead',
    count: 4,
    label: 'New leads need contact',
    description: '2 received in the last 2 hours and never contacted',
    urgency: 'urgent',
    recordSetId: 'new-leads',
  },
  {
    id: 'p-tasks',
    category: 'task',
    count: 3,
    label: 'Overdue tasks need resolution',
    description: 'Oldest is 5 days past due and blocking a partner review',
    urgency: 'urgent',
    recordSetId: 'overdue-tasks',
  },
  {
    id: 'p-realtors',
    category: 'realtor',
    count: 7,
    label: 'Realtor follow-ups are due',
    description: '2 partners have not been touched in over 10 days',
    urgency: 'attention',
    recordSetId: 'realtor-followups',
  },
  {
    id: 'p-preapprovals',
    category: 'preapproval',
    count: 3,
    label: 'Pre-approvals need review',
    description: '1 pre-approval expires in 6 days',
    urgency: 'attention',
    recordSetId: 'preapproval-reviews',
  },
  {
    id: 'p-milestones',
    category: 'milestone',
    count: 2,
    label: 'Loans require milestone updates',
    description: 'Borrowers and Realtors are waiting on a status change',
    urgency: 'normal',
    recordSetId: 'milestone-updates',
  },
]

export const goals: Goal[] = [
  {
    id: 'g-conversations',
    label: 'Consumer conversations',
    current: 12,
    target: 20,
    unit: 'conversations',
    recordSetId: 'consumer-conversations',
  },
  {
    id: 'g-touches',
    label: 'Realtor touches',
    current: 9,
    target: 15,
    unit: 'touches',
    recordSetId: 'realtor-touches',
  },
  {
    id: 'g-meetings',
    label: 'Partner meetings',
    current: 1,
    target: 2,
    unit: 'meetings',
    recordSetId: 'partner-meetings',
  },
  {
    id: 'g-preapprovals',
    label: 'Pre-approval reviews',
    current: 3,
    target: 3,
    unit: 'reviews',
    recordSetId: 'preapproval-reviews',
  },
  {
    id: 'g-overdue',
    label: 'Zero overdue tasks',
    current: 3,
    target: 0,
    unit: 'remaining',
    inverted: true,
    recordSetId: 'overdue-tasks',
  },
  {
    id: 'g-followups',
    label: 'Complete all priority follow-ups',
    current: 5,
    target: 5,
    unit: 'follow-ups',
    recordSetId: 'priority-followups',
  },
]

export const upNext = {
  contactName: 'Morgan Taylor',
  action: 'Call Morgan Taylor',
  reason:
    'New purchase lead received 18 minutes ago and has not been contacted.',
  phone: '(303) 555-0148',
  email: 'morgan.taylor@example.com',
  detail: 'Purchase · $485,000 · Conventional · Denver, CO',
  source: 'Zillow inquiry · Assigned to you at 9:41 AM',
  recordId: 'L-40219',
  impactLabel: 'Highest impact',
  slaLabel: 'Contact within 5 minutes to stay inside SLA',
}
